"use client";
import React, { createContext, useContext, useState, useEffect } from "react";
import { useGetUserAccountBalances } from "@/api/userApi";
import { useQueryClient } from "@tanstack/react-query";
import { isEqual } from "lodash";

interface User {
  email: string;
  first_name: string;
  last_name: string;
  unique_key: string;
  profile_image: string;
  trading_challenge: string;
  description: string;
  is_active: boolean;
  user_type: string;
  meta_data: string;
  created_at: string;
}

interface Preference {
  country: string;
  trading_yoe: string;
  risk_class: string;
  language: string;
  asset_class: string;
  notification_preference: string;
  nudges_tracker: string;
  walkthrough_status: string;
  webhook: string;
  send_email: string;
  socials: string;
}

interface UserData {
  user: User;
  preference: Preference;
  is_old_user: boolean;
  user_id: string;
  hmac: string;
  token: string;
  subscription: string;
  start_date: string;
  redirect_after_login: string;
  is_admin: boolean;
}

interface UserContextType {
  user: UserData | null;
  setUser: (user: UserData | null) => void;
  connectedBrokers: BrokerDetails;
  refreshConnectedBrokers: () => Promise<void>;
  balanceLoading: boolean;
  userLoading: boolean;
  setConnectedBrokers: (brokers: BrokerDetails) => void;
}
interface BrokerDetails {
  [brokerId: number]: {
    broker_id: number;
    broker_name: string;
    broker_image: string | null;
    type: string;
    balance: number;
    payload: {
      margin: Record<string, number | string>;
      main: Record<string, number | string>;
      trading: Record<string, number | string>;
    };
  };
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUserState] = useState<UserData | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [isExplicitLoading, setIsExplicitLoading] = useState(false);

  const {
    data: userAccountData,
    isPending,
    isSuccess,
    refetch,
  } = useGetUserAccountBalances(user?.user_id);

  const balanceLoading = isExplicitLoading || isPending;

  const [connectedBrokers, setConnectedBrokers] = useState<BrokerDetails>({});
  const queryClient = useQueryClient();
  const setUser = (newUser: UserData | null) => {
    setUserState(newUser);
    if (typeof window !== "undefined") {
      if (newUser) {
        localStorage.setItem("user-info", JSON.stringify(newUser));
      }
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedUser = localStorage.getItem("user-info");
      if (storedUser) {
        setUserState(JSON.parse(storedUser));
      }
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userAccountData?.data && isSuccess) {
      const mapped: BrokerDetails = userAccountData.data.reduce(
        (acc: any, broker: any) => {
          acc[broker.broker_id] = {
            ...broker,
            balance: broker?.payload?.main?.USD,
          };
          return acc;
        },
        {} as BrokerDetails,
      );

      if (!isEqual(mapped, connectedBrokers)) {
        setConnectedBrokers(mapped);
      }
      setIsExplicitLoading(false);
    }
  }, [userAccountData, balanceLoading, connectedBrokers]);

  const refreshConnectedBrokers = async () => {
    setIsExplicitLoading(true);
    await queryClient.invalidateQueries({
      queryKey: ["get_account_balances"],
    });
    await refetch();
    setIsExplicitLoading(false);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        connectedBrokers,
        refreshConnectedBrokers,
        balanceLoading,
        userLoading,
        setConnectedBrokers,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context)
    throw new Error("useUserContext must be used within a UserProvider");
  return context;
};
